import * as React from 'react';

import { Button } from '@/ui/button';
import { cn } from '@/utils';

import MathleteIcon from '../../assets/mathlete_101.png';

import { MathleteRegistrationDrawer } from './mathlete-registration-drawer';

/**
 * Card title for Mathlete 101 registration notification
 */
const MATHLETE_CARD_TITLE = 'Mathlete 101 Batch Selection';

/**
 * Card subtitle for Mathlete 101 registration notification
 */
const MATHLETE_CARD_SUBTITLE = 'Select your preferred batch to get started';

/**
 * Alt text for Mathlete card icon
 */
const MATHLETE_ICON_ALT = 'Mathlete 101 course icon';

/**
 * Mathlete 101 batch registration notification card
 *
 * Displays a notification card for users who are enrolled in Mathlete 101
 * but haven't selected a batch yet. Clicking "View" opens a drawer with
 * the batch selection interface.
 *
 * @example
 * ```tsx
 * <MathleteRegistrationCard ref={cardRef} />
 * ```
 */
export const MathleteRegistrationCard = React.forwardRef<HTMLDivElement>(
  (_, ref) => {
    const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

    const handleClick = React.useCallback(() => {
      setIsDrawerOpen(true);
    }, []);

    const handleCloseDrawer = React.useCallback(() => {
      setIsDrawerOpen(false);
    }, []);

    return (
      <>
        <div
          ref={ref}
          role="article"
          aria-labelledby="mathlete-registration-card-title"
          aria-describedby="mathlete-registration-card-description"
          className={cn(
            'bg-white/60 rounded-[14px] p-4',
            'flex items-center gap-4',
            'transition-all duration-200',
            'hover:shadow-lg'
          )}
        >
          {/* Icon Container */}
          <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
            <img
              src={MathleteIcon}
              alt={MATHLETE_ICON_ALT}
              className="w-12 h-12"
            />
          </div>

          {/* Content */}
          <div className="flex-1">
            <h3
              id="mathlete-registration-card-title"
              className="text-sm font-semibold text-gray-900"
            >
              {MATHLETE_CARD_TITLE}
            </h3>
            <p
              id="mathlete-registration-card-description"
              className="text-xs text-gray-600 mt-0.5"
            >
              {MATHLETE_CARD_SUBTITLE}
            </p>
          </div>

          {/* Action Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleClick}
            className="flex-shrink-0"
          >
            View
          </Button>
        </div>

        {/* Drawer */}
        <MathleteRegistrationDrawer
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
        />
      </>
    );
  }
);

MathleteRegistrationCard.displayName = 'MathleteRegistrationCard';
